// src/components/signup/SignupStep3.jsx
import React, { useState, useEffect } from 'react';
import usersAPI from '../../api/users';
import axios from 'axios';

export default function SignupStep3({ onComplete }) {
  const [preferences, setPreferences] = useState({
    breeds: [],
    minHeight: '',
    maxHeight: '',
    minAge: '',
    maxAge: '',
    temperament: '',
    disciplines: [],
    trainingLevel: [],
    minPrice: '',
    maxPrice: '',
  });

  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    smsAlerts: false,
    frequency: 'Daily',
  });

  const [breedsList, setBreedsList] = useState([]);
  const [disciplinesList, setDisciplinesList] = useState([]);
  const [temperamentsList, setTemperamentsList] = useState([]);
  const [trainingLevel, setTrainingList] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [breedRes, attrRes] = await Promise.all([
          axios.get('/api/breeds'),
          axios.get('/api/dropdowns/horseAttributes'),
        ]);

        console.log('✅ Breeds:', breedRes.data);
        console.log('✅ Attributes:', attrRes.data);

        const fields = attrRes.data || {};
        setBreedsList(breedRes.data);
        setDisciplinesList(attrRes.data.discipline || []);
        setTemperamentsList(attrRes.data.temperament || []);
        setTrainingList(attrRes.data.trainingLevel || []);
      } catch (err) {
        console.error('❌ Failed to fetch dropdowns:', err);
      }
    };

    fetchDropdowns();
  }, []);

  const handlePrefChange = (e) => {
    const { name, value } = e.target;
    setPreferences((prev) => ({ ...prev, [name]: value }));
  };

  const handleDisciplinesChange = (e) => {
    const value = e.target.value;
    setPreferences((prev) => ({
      ...prev,
      disciplines: prev.disciplines.includes(value)
        ? prev.disciplines.filter((d) => d !== value)
        : [...prev.disciplines, value],
    }));
  };

  const handleNotifChange = (e) => {
    const { name, type, checked, value } = e.target;
    setNotifications((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await usersAPI.updateProfile({ preferences, notifications });
      setMessage('✅ Preferences saved!');
      setTimeout(onComplete, 500);
    } catch (err) {
      console.error('Failed to update preferences:', err);
      setMessage('❌ Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      <h2 className="text-xl font-semibold mb-2">Horse Preferences</h2>

      <label>Preferred Breeds</label>
      <select
        multiple
        value={preferences.breeds}
        onChange={(e) => {
          const options = Array.from(e.target.selectedOptions).map((opt) => opt.value);
          setPreferences((prev) => ({ ...prev, breeds: options }));
        }}
        className="border p-2"
      >
        {breedsList.map((breed) => (
          <option key={breed._id} value={breed._id}>
            {breed.name}
          </option>
        ))}
      </select>

      <div className="grid grid-cols-2 gap-2">
        <input type="number" name="minHeight" value={preferences.minHeight} onChange={handlePrefChange} placeholder="Min Height (hands)" className="border p-2" />
        <input type="number" name="maxHeight" value={preferences.maxHeight} onChange={handlePrefChange} placeholder="Max Height (hands)" className="border p-2" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <input type="number" name="minAge" value={preferences.minAge} onChange={handlePrefChange} placeholder="Min Age" className="border p-2" />
        <input type="number" name="maxAge" value={preferences.maxAge} onChange={handlePrefChange} placeholder="Max Age" className="border p-2" />
      </div>

      <label>Temperament</label>
      <div className="flex flex-wrap gap-2">
        {temperamentsList.map((t) => (
          <label key={t} className="flex items-center gap-1">
            <input
              type="checkbox"
              value={t}
              checked={preferences.temperament.includes(t)}
              onChange={handleDisciplinesChange}
            />
            {t}
          </label>
        ))}
      </div>

      <label>Disciplines</label>
      <div className="flex flex-wrap gap-2">
        {disciplinesList.map((d) => (
          <label key={d} className="flex items-center gap-1">
            <input
              type="checkbox"
              value={d}
              checked={preferences.disciplines.includes(d)}
              onChange={handleDisciplinesChange}
            />
            {d}
          </label>
        ))}
      </div>

      <label>Training Level</label>
      <div className="flex flex-wrap gap-2">
        {trainingLevel.map((f) => (
          <label key={f} className="flex items-center gap-1">
            <input
              type="checkbox"
              value={f}
              checked={preferences.trainingLevel.includes(f)}
              onChange={handleDisciplinesChange}
            />
            {f}
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <input type="number" name="minPrice" value={preferences.minPrice} onChange={handlePrefChange} placeholder="Min Price" className="border p-2" />
        <input type="number" name="maxPrice" value={preferences.maxPrice} onChange={handlePrefChange} placeholder="Max Price" className="border p-2" />
      </div>

      <h2 className="text-xl font-semibold mt-4">Notification Preferences</h2>

      <label className="flex items-center gap-2">
        <input type="checkbox" name="emailAlerts" checked={notifications.emailAlerts} onChange={handleNotifChange} />
        Email Alerts
      </label>

      <label className="flex items-center gap-2">
        <input type="checkbox" name="smsAlerts" checked={notifications.smsAlerts} onChange={handleNotifChange} />
        SMS Alerts
      </label>

      <label>Alert Frequency</label>
      <select name="frequency" value={notifications.frequency} onChange={handleNotifChange} className="border p-2">
        <option>Instant</option>
        <option>Daily</option>
        <option>Weekly</option>
      </select>

      {message && <p className="text-center text-sm text-gray-600">{message}</p>}

      <button type="submit" disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded">
        {loading ? 'Saving...' : 'Save & Continue'}
      </button>
    </form>
  );
}